import { Link } from 'react-router';
import { DATA_SOURCES } from '../../../shared/domain/index.ts';
import { BROWSER_THIRD_PARTIES, HOST, PUBLISHER } from '../../../shared/legal.ts';
import { INDEPENDENCE_NOTICE, SITE_NAME } from '../../../shared/seo/pages.ts';
import { PageContainer } from '../../components/layout/PageContainer.tsx';
import { analyticsState } from '../../lib/analytics.ts';

function External({ href, children }: { href: string; children: string }) {
  return (
    <a href={href} rel="noopener noreferrer" target="_blank">
      {children}
      <span className="visually-hidden"> (nouvel onglet)</span>
    </a>
  );
}

/** Coordonnées de contact : e-mail et téléphone ne sont affichés que s'ils sont renseignés. */
function ContactLines() {
  if (PUBLISHER.email === '' && PUBLISHER.phone === '') return null;
  return (
    <>
      <dt>Contact</dt>
      <dd>
        {PUBLISHER.email !== '' && <a href={`mailto:${PUBLISHER.email}`}>{PUBLISHER.email}</a>}
        {PUBLISHER.email !== '' && PUBLISHER.phone !== '' && ' · '}
        {PUBLISHER.phone !== '' && <a href={`tel:${PUBLISHER.phone.replace(/\s/g, '')}`}>{PUBLISHER.phone}</a>}
      </dd>
    </>
  );
}

export function LegalNoticePage() {
  return (
    <PageContainer path="/mentions-legales">
      <section className="card prose" aria-labelledby="legal-publisher">
        <h2 id="legal-publisher">Éditeur du site</h2>
        <dl className="legal-list">
          <dt>Éditeur</dt>
          <dd>
            {PUBLISHER.name}, {PUBLISHER.status.toLowerCase()}
          </dd>
          <dt>Adresse</dt>
          <dd>{PUBLISHER.address}</dd>
          <ContactLines />
          <dt>Activité</dt>
          <dd>
            {PUBLISHER.activity} · <External href={PUBLISHER.website}>{PUBLISHER.website}</External>
          </dd>
          <dt>Identification</dt>
          <dd>
            SIREN {PUBLISHER.siren} · SIRET {PUBLISHER.siret}
          </dd>
          {PUBLISHER.vatNumber !== '' && (
            <>
              <dt>TVA intracommunautaire</dt>
              <dd>{PUBLISHER.vatNumber}</dd>
            </>
          )}
          <dt>Directeur de la publication</dt>
          <dd>{PUBLISHER.publicationDirector}</dd>
        </dl>
      </section>

      <section className="card prose" aria-labelledby="legal-host">
        <h2 id="legal-host">Hébergeur</h2>
        <dl className="legal-list">
          <dt>Hébergeur</dt>
          <dd>{HOST.name}</dd>
          <dt>Adresse</dt>
          <dd>{HOST.address}</dd>
          <dt>Téléphone</dt>
          <dd>{HOST.phone}</dd>
          <dt>Site</dt>
          <dd>
            <External href={HOST.website}>{HOST.website}</External>
          </dd>
        </dl>
      </section>

      <section className="card prose" aria-labelledby="legal-independence">
        <h2 id="legal-independence">Indépendance</h2>
        <p>
          <strong>{INDEPENDENCE_NOTICE}</strong> Aucun logo, blason ou élément graphique de la commune n’est utilisé.
          Les informations issues du site de la Ville sont reprises avec son accord, réduites au titre, à la date et au
          lien, et renvoient toujours vers ermont.fr.
        </p>
      </section>

      <section className="card prose" aria-labelledby="legal-ip">
        <h2 id="legal-ip">Propriété intellectuelle</h2>
        <p>
          Le code et l’interface de {SITE_NAME} sont l’œuvre de l’éditeur. Les données affichées appartiennent à leurs
          producteurs et restent soumises à leurs licences respectives, citées sur chaque bloc et rappelées sur la page{' '}
          <Link to="/a-propos">À propos</Link>.
        </p>
        <ul>
          {Object.values(DATA_SOURCES)
            .filter((source) => source.id !== 'fixture')
            .map((source) => (
              <li key={source.id}>
                <strong>{source.name}</strong> : {source.license.name} · {source.attribution}
              </li>
            ))}
        </ul>
      </section>

      <section className="card prose" aria-labelledby="legal-links">
        <h2 id="legal-links">Liens</h2>
        <p>
          Les liens vers d’autres sites sont proposés à titre informatif et s’ouvrent dans une nouvelle fenêtre.
          L’éditeur ne contrôle ni leur contenu ni leur disponibilité.
        </p>
      </section>
    </PageContainer>
  );
}

export function PrivacyPage() {
  const analytics = analyticsState();
  return (
    <PageContainer path="/confidentialite">
      <section className="card prose" aria-labelledby="privacy-summary">
        <h2 id="privacy-summary">L’essentiel</h2>
        <p>
          Pas de compte, pas de formulaire, pas de cookie, pas de réseau social intégré.{' '}
          {analytics === 'cloudflare'
            ? 'La fréquentation du site est mesurée sans cookie ni identifiant : aucun profil n’est constitué, et il n’y a donc pas de bandeau de consentement à cliquer.'
            : 'Aucune mesure d’audience n’est en place : la fréquentation du site n’est pas comptée.'}{' '}
          {SITE_NAME} ne cherche pas à savoir qui vous êtes et ne revend rien.
        </p>
      </section>

      <section className="card prose" aria-labelledby="privacy-controller">
        <h2 id="privacy-controller">Responsable du traitement</h2>
        <p>
          {PUBLISHER.name}, {PUBLISHER.address}
          {PUBLISHER.email !== '' && (
            <>
              {' · '}
              <a href={`mailto:${PUBLISHER.email}`}>{PUBLISHER.email}</a>
            </>
          )}
          .
        </p>
      </section>

      <section className="card prose" aria-labelledby="privacy-data">
        <h2 id="privacy-data">Données traitées</h2>
        <ul>
          <li>
            <strong>Journaux techniques de l’hébergeur</strong> : comme tout site, l’affichage d’une page suppose que
            votre adresse IP soit traitée par {HOST.name}, pour acheminer la réponse et protéger le service contre les
            abus. Base légale : intérêt légitime à fournir un service en état de marche. Ces journaux sont conservés par
            l’hébergeur selon sa propre politique ; l’éditeur ne les exploite pas à d’autres fins et ne constitue aucun
            profil.
          </li>
          {analytics === 'cloudflare' && (
            <li>
              <strong>Mesure d’audience</strong> : le nombre de pages vues, la provenance des visites et les temps de
              chargement sont comptés par Cloudflare Web Analytics,{' '}
              <strong>sans cookie ni identifiant persistant</strong>. Ces mesures sont agrégées : elles ne permettent ni
              de vous reconnaître, ni de vous suivre d’une visite à l’autre.
            </li>
          )}
          <li>
            <strong>Préférences enregistrées dans votre navigateur</strong> : thème clair ou sombre, gare favorite,
            calques de carte. Elles restent sur votre appareil (stockage local), ne sont jamais envoyées au serveur, et
            disparaissent si vous effacez les données du site. Ce ne sont pas des cookies publicitaires : aucun bandeau
            de consentement n’est donc nécessaire.
          </li>
          <li>
            <strong>Votre position</strong> : elle n’est jamais demandée ni transmise.
          </li>
        </ul>
      </section>

      <section className="card prose" aria-labelledby="privacy-third-parties">
        <h2 id="privacy-third-parties">Services tiers contactés par votre navigateur</h2>
        <p>
          Les sources de données (transports, météo, qualité de l’air, vigilance, agenda, actualités, services publics)
          sont interrogées <strong>par notre serveur</strong> : elles ne voient jamais votre adresse IP. Les services
          suivants sont en revanche contactés directement par votre navigateur, et reçoivent donc votre adresse IP :
        </p>
        <ul>
          {BROWSER_THIRD_PARTIES.filter(
            (third) => third.name !== 'Cloudflare Web Analytics' || analytics === 'cloudflare',
          ).map((third) => (
            <li key={third.name}>
              <External href={third.url}>{third.name}</External> : {third.purpose}.
            </li>
          ))}
        </ul>
      </section>

      <section className="card prose" aria-labelledby="privacy-rights">
        <h2 id="privacy-rights">Vos droits</h2>
        <p>
          Vous disposez des droits d’accès, de rectification, d’effacement, de limitation et d’opposition prévus par le
          RGPD. En pratique, {SITE_NAME} ne conserve aucune donnée permettant de vous identifier : une demande portera
          donc surtout sur les journaux de l’hébergeur.
          {PUBLISHER.email !== '' && (
            <>
              {' '}
              Écrivez à <a href={`mailto:${PUBLISHER.email}`}>{PUBLISHER.email}</a>.
            </>
          )}{' '}
          Vous pouvez également saisir la{' '}
          <External href="https://www.cnil.fr">Commission nationale de l’informatique et des libertés (CNIL)</External>.
        </p>
      </section>
    </PageContainer>
  );
}

export function TermsPage() {
  return (
    <PageContainer path="/cgu">
      <section className="card prose" aria-labelledby="terms-object">
        <h2 id="terms-object">Objet</h2>
        <p>
          {SITE_NAME} rassemble des informations publiques sur Ermont (Val-d’Oise) : météo, qualité de l’air,
          transports, vie locale. L’accès est libre et gratuit, sans compte.
        </p>
      </section>

      <section className="card prose" aria-labelledby="terms-accuracy">
        <h2 id="terms-accuracy">Fiabilité des informations</h2>
        <p>
          Les informations sont <strong>indicatives</strong>. Elles proviennent de sources publiques, affichées avec
          leur heure de mise à jour, et peuvent être erronées, incomplètes ou indisponibles, notamment en cas de panne
          d’un fournisseur. Une donnée ancienne est signalée comme telle, une donnée manquante n’est jamais remplacée
          par une valeur inventée.
        </p>
        <p>
          Avant un déplacement, une démarche ou toute décision importante, vérifiez auprès de la source officielle :
          Île-de-France Mobilités pour les trains, Météo-France pour la vigilance, la Ville d’Ermont pour la vie
          municipale. L’éditeur ne peut être tenu responsable d’un dommage résultant de l’usage de ces informations.
        </p>
      </section>

      <section className="card prose" aria-labelledby="terms-availability">
        <h2 id="terms-availability">Disponibilité</h2>
        <p>
          Le service est fourni en l’état, sans garantie de disponibilité. Il peut être interrompu, modifié ou arrêté à
          tout moment, notamment si une source cesse d’être accessible ou si son éditeur en demande le retrait.
        </p>
      </section>

      <section className="card prose" aria-labelledby="terms-usage">
        <h2 id="terms-usage">Utilisation du site</h2>
        <p>
          Vous pouvez consulter, citer et partager les pages du site. La réutilisation des données affichées reste
          soumise aux licences de leurs producteurs, rappelées sur la page <Link to="/a-propos">À propos</Link> et dans
          les <Link to="/mentions-legales">mentions légales</Link>. Toute tentative de perturber le service (extraction
          massive automatisée, contournement des limitations) n’est pas autorisée.
        </p>
      </section>

      <section className="card prose" aria-labelledby="terms-law">
        <h2 id="terms-law">Droit applicable</h2>
        <p>
          Les présentes conditions sont soumises au droit français. Pour toute question, contactez l’éditeur indiqué
          dans les <Link to="/mentions-legales">mentions légales</Link>.
        </p>
      </section>
    </PageContainer>
  );
}
